import React from 'react';
import { Alert, Box, Checkbox, Divider, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import { formatAmount, formatDate, getLineNumber, isRowSelectable } from './bankCsvImportUtils';

export default function BankCsvPreviewSection({
    previewData,
    rows,
    selection,
    selectableCount,
    selectedCount,
    allSelected,
    someSelected,
    onToggleAll,
    onToggleRow,
    onRowClick,
}) {
    if (!previewData) {
        return null;
    }

    return (
        <Box>
            <Divider sx={{ my: 2 }} />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                <Typography variant="h6">Vorschau</Typography>
                <Typography variant="body2" color="text.secondary">
                    {previewData.validRows ?? 0} valide, {previewData.errorRows ?? 0} fehlerhaft, {previewData.totalRows ?? rows.length} gesamt
                </Typography>
                {selection && (
                    <Typography variant="body2" color="text.secondary">
                        Auswahl: {selectedCount} von {selectableCount}
                    </Typography>
                )}
            </Stack>
            {rows.length ? (
                <Box sx={{ overflowX: 'auto', mt: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={Boolean(allSelected)}
                                        indeterminate={Boolean(someSelected)}
                                        onChange={onToggleAll}
                                        inputProps={{ 'aria-label': 'Alle auswaehlen' }}
                                    />
                                </TableCell>
                                <TableCell>Zeile</TableCell>
                                <TableCell>Buchungsdatum</TableCell>
                                <TableCell>Valutadatum</TableCell>
                                <TableCell align="right">Betrag</TableCell>
                                <TableCell>Waehrung</TableCell>
                                <TableCell>Verwendungszweck</TableCell>
                                <TableCell>Empfaenger</TableCell>
                                <TableCell>IBAN</TableCell>
                                <TableCell>BIC</TableCell>
                                <TableCell>Kategorie</TableCell>
                                <TableCell>Referenz</TableCell>
                                <TableCell>Fehler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, index) => {
                                const hasErrors = Array.isArray(row.errors) && row.errors.length > 0;
                                const lineNumber = getLineNumber(row, index);
                                const selectable = isRowSelectable(row);
                                return (
                                    <TableRow
                                        key={`${lineNumber}`}
                                        onClick={() => onRowClick(row, index)}
                                        sx={{
                                            ...(hasErrors && {
                                                backgroundColor: 'rgba(244,67,54,0.06)',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(244,67,54,0.12)',
                                                },
                                            }),
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={Boolean(selection && selection.has(lineNumber))}
                                                disabled={!selectable}
                                                onChange={() => onToggleRow(row, index)}
                                                inputProps={{ 'aria-label': `Datensatz ${lineNumber}` }}
                                            />
                                        </TableCell>
                                        <TableCell>{lineNumber}</TableCell>
                                        <TableCell>{formatDate(row.bookingDate)}</TableCell>
                                        <TableCell>{formatDate(row.valueDate)}</TableCell>
                                        <TableCell align="right">{formatAmount(row.amount, row.currency)}</TableCell>
                                        <TableCell>{row.currency || '-'}</TableCell>
                                        <TableCell>{row.purposeText || '-'}</TableCell>
                                        <TableCell>{row.counterpartyName || '-'}</TableCell>
                                        <TableCell>{row.iban || '-'}</TableCell>
                                        <TableCell>{row.bic || '-'}</TableCell>
                                        <TableCell>{row.category || '-'}</TableCell>
                                        <TableCell>{row.referenceId || '-'}</TableCell>
                                        <TableCell>{hasErrors ? row.errors.join('; ') : '-'}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
            ) : (
                <Alert severity="info" sx={{ mt: 2 }}>
                    Keine Transaktionen erkannt.
                </Alert>
            )}
        </Box>
    );
}
